/**
 * Кнопка только с иконкой. `label` обязателен и уходит в aria-label:
 * без него кнопка невидима для скринридера — иконка текстом не является.
 */

import type { LucideIcon } from "lucide-react";
import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes } from "react";

import { cn } from "./cn";

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  icon: LucideIcon;
  /** Текстовое название действия. Обязательно — это и есть доступное имя. */
  label: string;
  /** Пояснение при наведении. По умолчанию совпадает с label, но отключённой
   *  кнопке нужно объяснить ПРИЧИНУ, а label этого сделать не может. */
  title?: string | undefined;
  variant?: "ghost" | "secondary" | "danger";
  size?: "sm" | "md";
  loading?: boolean;
  className?: string | undefined;
}

const VARIANTS = {
  ghost: "text-muted hover:bg-sunken hover:text-fg",
  secondary: "border border-line bg-surface text-fg hover:bg-sunken",
  danger: "text-danger hover:bg-danger-soft",
} as const;

export function IconButton({
  icon: Icon,
  label,
  title,
  variant = "ghost",
  size = "md",
  loading = false,
  disabled,
  className,
  type = "button",
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      aria-label={label}
      title={title ?? label}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-md transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "size-7" : "size-8",
        // Цель касания не меньше 44 px (docs/08-mobile-roadmap.md, M2): под
        // пальцем кнопку расширяет невидимый псевдоэлемент, вид не меняется.
        // Псевдоэлементу нужен позиционированный родитель; кнопке, которую
        // вызывающий код уже поставил absolute, relative не добавляем.
        className?.includes("absolute") !== true && "relative",
        "pointer-coarse:after:absolute pointer-coarse:after:content-['']",
        size === "sm" ? "pointer-coarse:after:-inset-2" : "pointer-coarse:after:-inset-1.5",
        VARIANTS[variant],
        className,
      )}
    >
      {loading ? (
        <Loader2 className="size-4 animate-spin" aria-hidden />
      ) : (
        <Icon className="size-4" aria-hidden />
      )}
    </button>
  );
}
